export const ACCOUNT_SUPPORT_EMAIL = 'support@bravosfit.com';
export const ACCOUNT_SITE_URL = 'https://bravosfit.com';
export const ACCOUNT_PRIVACY_URL = 'https://bravosfit.com/privacy';
export const ACCOUNT_TERMS_URL = 'https://bravosfit.com/terms';

/** App Store URL when live; empty → invite landing shows Coming Soon under iOS badge. */
export const ACCOUNT_IOS_APP_STORE_URL = (
  process.env.REACT_APP_IOS_APP_STORE_URL || ''
).trim();

/** Google Play URL when live; empty → invite landing shows Coming Soon under Play badge. */
export const ACCOUNT_ANDROID_PLAY_STORE_URL = (
  process.env.REACT_APP_ANDROID_PLAY_STORE_URL || ''
).trim();

export function accountInviteUrl(code: string): string {
  const trimmed = code.trim();
  return `${ACCOUNT_SITE_URL}/invite/${encodeURIComponent(trimmed)}`;
}

/** Caption only — use with a separate `url` in Web Share / ShareLink so the link isn’t duplicated. */
export const ACCOUNT_INVITE_SHARE_CAPTION = 'I’m training with Bravos. Join me:';

export function accountInviteShareMessage(code: string): string {
  return `${ACCOUNT_INVITE_SHARE_CAPTION} ${accountInviteUrl(code)}`;
}

/** @deprecated Prefer accountInviteShareMessage(code) so shares stay attributable. */
export const ACCOUNT_INVITE_MESSAGE =
  'I’m training with Bravos. Join me: https://bravosfit.com';

export const ACCOUNT_FAQ: { question: string; answer: string }[] = [
  {
    question: 'How do I find workouts?',
    answer:
      'Use Home for your scheduled sessions and Discover to browse and add workouts to your calendar.',
  },
  {
    question: 'What’s the difference between Home and Discover?',
    answer:
      'Home shows what’s on your calendar today and upcoming. Discover is where you browse workouts and plans to add to your schedule.',
  },
  {
    question: 'How do I reschedule or move a workout?',
    answer:
      'Open a scheduled workout from Home or your calendar, then choose Reschedule and pick a new date.',
  },
  {
    question: 'How do I connect with a trainer?',
    answer:
      'For now, you can only connect with a trainer when they invite you, and you’ll see the request in Notifications. Accept it there to start working together. Soon, you’ll also be able to search for trainers in the app.\n\nIf you don’t have any trainers connected yet, check out the Discover tab to start working out with our wide range of workouts on your own.',
  },
  {
    question: 'Can I change my display name?',
    answer:
      'Yes. Open Account from Home (in the app) and tap the edit button next to your name, then save your preferred first and last name.',
  },
  {
    question: 'How do I manage notifications?',
    answer:
      'Open Account → Communication preferences to control push alerts and tips. On iPhone, if you previously chose Don’t Allow for push, turn notifications on for Bravos in iOS Settings.',
  },
  {
    question: 'How do tips and offers work?',
    answer:
      'Tips, offers, and product updates are optional. Turn them on under Communication preferences. Account and security emails are always sent when needed.',
  },
  {
    question: 'Can I use Bravos on web and iOS?',
    answer:
      'Yes. Sign in with the same account on either. Your profile and communication preferences sync to your account.',
  },
  {
    question: 'How do I sign out?',
    answer: 'Open Account and tap Sign out at the bottom of the page.',
  },
  {
    question: 'How do I delete my account?',
    answer:
      'On Account, open Danger zone → Delete account and follow the confirmation steps. This can’t be undone.',
  },
  {
    question: 'Who do I contact for help?',
    answer: `Use Contact us on this page, or email ${ACCOUNT_SUPPORT_EMAIL}.`,
  },
];

export const ACCOUNT_SETTINGS_TITLES: Record<string, string> = {
  'help-feedback': 'Help & Feedback',
  'help-center': 'Help & Feedback',
  feedback: 'Help & Feedback',
  'invite-friends': 'Invite friends',
  'communication-preferences': 'Communication preferences',
};

export function accountMailto(subject: string, body = ''): string {
  // encodeURIComponent keeps newlines as %0A (URLSearchParams uses + for spaces, which Mail mishandles).
  let url = `mailto:${ACCOUNT_SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  if (body) {
    url += `&body=${encodeURIComponent(body)}`;
  }
  return url;
}

function webOsAndDevice(): { osVersion: string; device: string } {
  if (typeof navigator === 'undefined') {
    return { osVersion: 'unknown', device: 'Web' };
  }
  const ua = navigator.userAgent;
  let osVersion = 'Web';
  if (/iPhone|iPad|iPod/.test(ua)) {
    const m = ua.match(/OS (\d+[._]\d+(?:[._]\d+)?)/);
    osVersion = m ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
  } else if (/Mac OS X/.test(ua)) {
    const m = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/);
    osVersion = m ? `macOS ${m[1].replace(/_/g, '.')}` : 'macOS';
  } else if (/Android/.test(ua)) {
    const m = ua.match(/Android (\d+(?:\.\d+)?)/);
    osVersion = m ? `Android ${m[1]}` : 'Android';
  } else if (/Windows NT/.test(ua)) {
    const m = ua.match(/Windows NT (\d+\.\d+)/);
    osVersion = m ? `Windows ${m[1]}` : 'Windows';
  } else if (/CrOS/.test(ua)) {
    osVersion = 'Chrome OS';
  }
  const device =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ||
    navigator.platform ||
    'Web';
  return { osVersion, device: `${device}; ${ua.slice(0, 120)}` };
}

/** Contact support mailto with app/device debug details for bug reports. */
export function accountSupportMailto(
  memberId?: string | null,
  appVersion = '1.0.0'
): string {
  return accountMailto('Bravos Help', accountSupportDebugBody(memberId, appVersion));
}

export function accountSupportDebugBody(
  memberId?: string | null,
  appVersion = '1.0.0'
): string {
  const mid = memberId?.trim() || 'unknown';
  const { osVersion, device } = webOsAndDevice();
  // ZWSP keeps a blank first line — many mail apps strip leading newlines otherwise.
  return `\u200B\r\n---\r\nMy Bravos user details:\r\nApp version: ${appVersion}\r\nMember ID: ${mid}\r\nOS version: ${osVersion}\r\nDevice: ${device}`;
}
